import dayjs from 'dayjs';
import { t } from 'i18next';
import * as yup from 'yup';

import { testRequiredSchema } from 'core/schemas';

import { ShipSelection } from './enums';

const isDateNotBeforeToday = (value: unknown): boolean => {
  if (!value) {
    return true;
  }

  return (
    dayjs(value as string)
      .startOf('day')
      .valueOf() >= dayjs().startOf('day').valueOf()
  );
};

// Shared Ship Selection validation: shipSelection ∈ 5; per-option required dates
// (earliest for Within/On/After, requested for By/Within); each used date ≥ today;
// Within → requested ≥ earliest. `active` gates the required/ordering rules (the DDP/order path).
// ≥ today only applies to a date the selection uses: NDD's earliest is the API-owned submission
// day (can be a stale/frozen past date) and the BE skips the check for it.
const EARLIEST_SELECTIONS: ShipSelection[] = [
  ShipSelection.DELIVERY_WITHIN,
  ShipSelection.DELIVERY_ON,
  ShipSelection.DELIVERY_AFTER,
];
const REQUESTED_SELECTIONS: ShipSelection[] = [
  ShipSelection.DELIVERY_BY,
  ShipSelection.DELIVERY_WITHIN,
];

export const shipSelectionSchema = (active = false) => ({
  shipSelection: yup
    .string()
    .label(t('labelShipSelection'))
    .nullable()
    .oneOf(Object.values(ShipSelection))
    .test(...testRequiredSchema(active, t('labelShipSelection'))),
  earliestAcceptableDate: yup
    .string()
    .nullable()
    .test(
      'required',
      t('errorFieldCannotBeEmpty', { field: t('labelEarliestAcceptableDate') }),
      function (value) {
        if (!active) {
          return true;
        }

        return EARLIEST_SELECTIONS.includes(this.parent.shipSelection) ? Boolean(value) : true;
      }
    )
    .test(
      'not-before-today',
      t('errorDateNotBeforeToday', { label: t('labelEarliestAcceptableDate') }),
      function (value) {
        return (
          !EARLIEST_SELECTIONS.includes(this.parent.shipSelection) || isDateNotBeforeToday(value)
        );
      }
    ),
  requiredOnSiteDate: yup
    .string()
    .nullable()
    .test(
      'required',
      t('errorFieldCannotBeEmpty', { field: t('labelRequiredOnSiteDate') }),
      function (value) {
        if (!active) {
          return true;
        }

        return REQUESTED_SELECTIONS.includes(this.parent.shipSelection) ? Boolean(value) : true;
      }
    )
    .test(
      'not-before-today',
      t('errorDateNotBeforeToday', { label: t('labelRequiredOnSiteDate') }),
      function (value) {
        return (
          !REQUESTED_SELECTIONS.includes(this.parent.shipSelection) || isDateNotBeforeToday(value)
        );
      }
    )
    .test('after-earliest', t('errorRequestedBeforeEarliestDate'), function (value) {
      if (!active || this.parent.shipSelection !== ShipSelection.DELIVERY_WITHIN) {
        return true;
      }

      const earliest = this.parent.earliestAcceptableDate;

      if (!value || !earliest) {
        return true;
      }

      return dayjs(value).startOf('day').valueOf() >= dayjs(earliest).startOf('day').valueOf();
    }),
});
